/**
 * @file 积分详情组件（调度层 + 两个模式组件）
 *  - 首页（personal）：项目下拉选择器 + 流水表格 + 提取按钮
 *  - 工作台（project）：当前项目信息 + 流水表格 + 提取按钮
 */
import {useState, useCallback, useEffect} from 'react';
import ProjectInfoBar from './ProjectInfoBar';
import TransactionPanel from './TransactionPanel';
import styles from './PointsDetail.module.less';
import {useProjectSummary} from '@/hooks/points/useProjectSummary';
import {
    PROJECT_TRANSACTIONS_PAGE_SIZE,
    useProjectTransactions,
} from '@/hooks/points/useProjectTransactions';
import {useRedeemPoints} from '@/hooks/points/useRedeemPoints';
import type {ProjectId} from '@/types';
import type {ProjectTransactionsTab} from '@/services/points/interface';
import type {PointsModalPayload} from '@/store/useModalStore';

type PointsDetailProps = PointsModalPayload;

// ─── PersonalDetail（首页模式）────────────────────────────────
const PersonalDetail: React.FC = () => {
    const {projects} = useProjectSummary();
    const [selectedProjectId, setSelectedProjectId] = useState<ProjectId | undefined>(undefined);
    const redeemMutation = useRedeemPoints();
    const [resetSignal, setResetSignal] = useState(0);
    const [activeTab, setActiveTab] = useState<ProjectTransactionsTab>(0);

    // 打开弹窗后首次有数据时选第一项；列表因 refetch 变化时若当前项仍在则保留（避免提取成功后误切到第一项）
    useEffect(() => {
        if (projects.length === 0) {
            setSelectedProjectId(undefined);
            return;
        }
        setSelectedProjectId((prev) => {
            if (projects.some((p) => p.projectId === prev)) {
                return prev;
            }
            return projects[0].projectId;
        });
    }, [projects]);

    const project = projects.find((p) => p.projectId === selectedProjectId);

    const handleRedeem = useCallback(async () => {
        if (selectedProjectId === undefined) {
            return;
        }
        await redeemMutation.mutateAsync({projectId: selectedProjectId});
        setResetSignal((s) => s + 1);
    }, [selectedProjectId, redeemMutation]);

    return (
        <div className={styles.personalContainer}>
            {selectedProjectId !== undefined && project && (
                <ProjectInfoBar
                    project={project}
                    projectSelector={{
                        projects,
                        selectedProjectId,
                        onProjectChange: (id: ProjectId) => {
                            setSelectedProjectId(id);
                            setActiveTab(0);
                        },
                    }}
                    isRedeeming={redeemMutation.isPending}
                    onRedeem={handleRedeem}
                    className={styles.personalProjectInfoBar}
                />
            )}
            <TransactionPanel
                key={selectedProjectId}
                projectId={selectedProjectId}
                resetSignal={resetSignal}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />
        </div>
    );
};

// ─── ProjectDetail（工作台模式）───────────────────────────────
const ProjectDetail: React.FC<{projectId: ProjectId}> = ({projectId}) => {
    const {project} = useProjectTransactions(projectId, PROJECT_TRANSACTIONS_PAGE_SIZE, 0);
    const redeemMutation = useRedeemPoints();
    const [resetSignal, setResetSignal] = useState(0);
    const [activeTab, setActiveTab] = useState<ProjectTransactionsTab>(0);

    const handleRedeem = useCallback(async () => {
        if (!projectId) {
            return;
        }
        await redeemMutation.mutateAsync({projectId});
        setResetSignal((s) => s + 1);
    }, [projectId, redeemMutation]);

    return (
        <div className={styles.projectContainer}>
            {project && (
                <ProjectInfoBar
                    project={project}
                    isRedeeming={redeemMutation.isPending}
                    onRedeem={handleRedeem}
                    className={styles.projectProjectInfoBar}
                />
            )}
            <TransactionPanel
                key={projectId}
                projectId={projectId}
                resetSignal={resetSignal}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />
        </div>
    );
};

const PointsDetail: React.FC<PointsDetailProps> = (props) => {
    if (props.type === 'project') {
        return <ProjectDetail projectId={props.projectId} />;
    }
    return <PersonalDetail />;
};

export default PointsDetail;
